/**
 * The pacing driver (D153) — the metronome, as a hook: convert the player's
 * silence into `wait` at the pack's declared pace.
 *
 * @remarks
 * The engine's clock is turns and it never reads a wall clock; this is the
 * one place seconds exist, and they become turns before they reach the store
 * — a `wait` dispatched through the same door the keyboard uses, so the
 * transcript, the replay and the export all read what happened as exactly
 * what happened: a wait. The session log of a player who idled to death IS
 * twelve waits (the clock conference, §2).
 *
 * **Metronome, not idle timer** (Mike's ruling): the wall clock keeps running
 * while the player types. A slow reader and a fast typist are under the same
 * pressure, which is the hole he asked closed.
 *
 * What the shell owes, and must never do (conference §5):
 *
 * - **Never accrue.** One armed timeout, re-armed from zero after every
 *   ANSWERED input — a refusal, a failed parse, a question as much as a
 *   turn, because the player acted either way (Mike, 2026-11-01: in the
 *   Breach a refused command left the last turn's silence running, and the
 *   clock's `wait` landed a moment after he typed). A tab that was hidden —
 *   a laptop that slept — owes no turns when it comes back. It re-arms from
 *   zero then, too.
 * - **Suspend** while the document is hidden, while a dialog is open, while
 *   the session is not ready, and whenever `resolvePacing` says no clock
 *   runs here — or the player's preference says none runs anywhere.
 * - **Defer, not skip,** while the reveal is still typing (D102: the player
 *   has not been able to read the last reading yet) and while the input
 *   line holds words. A turn landing mid-sentence is the cruel case.
 *
 * A parked question survives an auto-`wait` with no engine change:
 * `isAnswerShaped` returns false for anything that parses as a complete
 * command, and `wait` does.
 *
 * @packageDocumentation
 */

import type { GameDescription, PacingView } from '@orpheus/api-contracts';

import { type AutoWaitPreference, type GameStore, submitPlayerInput } from '@orpheus/game-store';
import { useEffect, useRef, useState } from 'react';

/** What the driver hands the view: the resolved clock, when this silence began, and which echoes it typed. */
export interface PacingDriverOutput {
	/** Ids of the player echoes the clock typed — rendered muted. */
	readonly mutedEntryIds: ReadonlySet<string>;
	/** `performance.now()` at the start of the current silence, while the metronome is armed. */
	readonly turnStartedAt: number | undefined;
}

/** Inputs the driver reads from the view. */
export interface PacingDriverInputs {
	/** The typed-but-unsent words: a non-empty line defers the tick. */
	readonly inputValue: string;
	/** Whether silence costs turns at all — the pack's default under the player's word (see {@link isClockEnabledFor}). */
	readonly isClockEnabled: boolean;
	/** The clock as the view carries it right now; `undefined` while none runs here. */
	readonly pacing: PacingView | undefined;
	/** How long the arriving lines take to finish revealing — the tick waits for the reading. */
	readonly revealMs: number;
	readonly store: GameStore;
	/** How many entries the view's transcript holds — every answered input lengthens it, and re-arms the silence from zero. */
	readonly transcriptLength: number | undefined;
}

/** How long a deferred tick waits before looking again. */
const DEFERRAL_MS = 1000;

/**
 * The pack's default under the player's word: `'default'` defers to the
 * pack, `'on'` and `'off'` override it. A pack with no clock is never on.
 *
 * @param pacing - The pack's pacing block, if it declares one
 * @param preference - The player's stored preference
 * @returns Whether silence costs turns
 */
export const isClockEnabledFor = (clock: GameDescription['clock'], preference: AutoWaitPreference): boolean =>
	clock !== undefined && (preference === 'default' ? clock.enabledByDefault : preference === 'on');

/** Whether the document is on screen right now — the shell's suspend condition. */
const readIsDocumentVisible = (): boolean => document.visibilityState === 'visible';

/** Whether any dialog stands open — the map, the journal, the voices: reading, not playing. */
const isDialogOpen = (): boolean => document.querySelector('dialog[open]') !== null;

/**
 * Run the metronome.
 *
 * @param inputs - {@link PacingDriverInputs}
 * @returns {@link PacingDriverOutput}
 */
export const usePacingDriver = ({ inputValue, isClockEnabled, pacing, revealMs, store, transcriptLength }: PacingDriverInputs): PacingDriverOutput => {
	const [isDocumentVisible, setIsDocumentVisible] = useState(readIsDocumentVisible);
	const [turnStartedAt, setTurnStartedAt] = useState<number | undefined>(undefined);
	const [mutedEntryIds, setMutedEntryIds] = useState<ReadonlySet<string>>(() => new Set());
	// Read at fire time, never captured: the tick must see the words typed
	// AFTER it was armed.
	const inputValueRef = useRef(inputValue);

	inputValueRef.current = inputValue;

	useEffect(() => {
		const onVisibilityChange = () => {
			setIsDocumentVisible(readIsDocumentVisible());
		};

		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, []);

	const autoWaitMs = pacing?.autoWaitMs;
	const isRunning = isClockEnabled && isDocumentVisible && autoWaitMs !== undefined && transcriptLength !== undefined;

	// One armed timeout. Every dependency change — an answered input, a
	// preference, a tab coming back — clears it and starts the silence over from zero.
	useEffect(() => {
		if (!isRunning) {
			setTurnStartedAt(undefined);

			return undefined;
		}

		setTurnStartedAt(performance.now());

		let timeout: ReturnType<typeof setTimeout> | undefined;

		const tick = () => {
			// Deferrals, not skips: look again in a moment.
			if (inputValueRef.current.trim() !== '' || isDialogOpen() || !readIsDocumentVisible()) {
				timeout = setTimeout(tick, DEFERRAL_MS);

				return;
			}

			// The clock's echo is muted by its id, which the view after the turn
			// names: the first player line past the ones there were.
			const entriesBefore = store.getState().session.view?.transcript.length ?? 0;

			void store.dispatch(submitPlayerInput('wait')).then((outcome) => {
				const echo = outcome.view.transcript.slice(entriesBefore).find((entry) => entry.source === 'player');

				if (echo !== undefined) {
					setMutedEntryIds((previous) => new Set([...previous, echo.id]));
				}
			}, () => undefined);
		};

		// The reading comes first: a tick never lands while the last line is still typing.
		timeout = setTimeout(tick, Math.max(autoWaitMs, revealMs));

		return () => {
			clearTimeout(timeout);
		};
	}, [autoWaitMs, isRunning, revealMs, store, transcriptLength]);

	return { mutedEntryIds, turnStartedAt };
};
